<template>
  <div class="page-container billing-reports-page" ref="page">
    <portal to="page-name">Relatórios do faturamento</portal>
    <div class="card card-page" v-if="hasAccess">
      <div class="card-header">
        <h1 class="card-title">Relatórios do faturamento</h1>
      </div>
      <div class="card-body">
        <div class="br-layout">
          <div class="br-side">
            <div class="br-group" v-for="group in groups" :key="group.key">
              <div class="br-group-title">{{ group.label }}</div>
              <ul class="menu br-menu">
                <li class="menu-item" v-for="report in group.reports" :key="report.key">
                  <a href="#" :class="{'is-selected': selected === report.key}"
                     @click.prevent="select(report)">
                    {{ report.label }}
                    <span class="br-soon" v-if="!report.ready">em breve</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="br-content">
            <template v-if="current">
              <div class="br-content-head">
                <h2 class="br-content-title">{{ current.label }}</h2>
                <div class="br-content-text">{{ current.description }}</div>
              </div>

              <component :is="current.component" v-if="current.component"/>

              <div class="empty br-empty" v-else>
                <div class="empty-icon"><fa-icon :icon="['fal', 'clock']" size="2x"/></div>
                <p class="empty-title">Relatório em construção</p>
                <p class="empty-subtitle">{{ current.description }}</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <forbidden v-else></forbidden>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { REPORT_BILLING_RESULTS } from '@/data/actions/modules/financial';
import { isEnabled as isInsuranceBillingEnabled } from '@/app/billing/guide/service';
import Forbidden from '@/components/auth/Forbidden.vue';
import { GROUPS, REPORTS } from '../reports';

export default {
  components: {
    Forbidden,
  },
  data() {
    return {
      selected: null,
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    hasAccess() {
      return this.$can(REPORT_BILLING_RESULTS) && isInsuranceBillingEnabled(this.user);
    },
    available() {
      return REPORTS
        .filter(report => !report.permission || this.$can(report.permission))
        .map(report => ({ ...report, ready: !!report.component }));
    },
    groups() {
      return GROUPS
        .map(group => ({
          ...group,
          reports: this.available.filter(report => report.group === group.key),
        }))
        .filter(group => group.reports.length > 0);
    },
    current() {
      return this.available.find(report => report.key === this.selected) || null;
    },
  },
  mounted() {
    const first = this.available[0];
    if (first) this.selected = first.key;
  },
  methods: {
    select(report) {
      this.selected = report.key;
      this.scrollTop();
    },
    scrollTop() {
      const { page } = this.$refs;
      if (!page) return;
      page.scrollIntoView({ block: 'start', behavior: 'smooth' });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables";

.billing-reports-page {
  .br-layout {
    display: grid;
    gap: $layout-spacing-lg;
    grid-template-columns: 15rem minmax(0, 1fr);
  }
  .br-side {
    border-right: $border-color solid $border-width;
    padding-right: $layout-spacing-lg;
  }
  .br-group + .br-group {
    margin-top: $layout-spacing-lg;
  }
  .br-group-title {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
  }
  .br-menu {
    background: transparent;
    box-shadow: none;
    padding: $layout-spacing-sm 0 0;
    .menu-item > a {
      align-items: baseline;
      display: flex;
      font-size: $font-size-sm;
      gap: $layout-spacing-sm;
      justify-content: space-between;
      &:focus {
        box-shadow: none;
      }
      &:hover {
        background-color: $gray-color-ultra-light;
        color: $body-font-color;
      }
      &.is-selected {
        background-color: lighten($primary-color, 36%);
        color: $secondary-color-dark;
        font-weight: 600;
      }
    }
    .br-soon {
      color: $gray-color;
      font-size: $font-size-xs;
      white-space: nowrap;
    }
  }
  .br-content-head {
    border-bottom: $border-color solid $border-width;
    padding-bottom: $layout-spacing;
  }
  .br-content-title {
    font-size: $font-size-lg;
    font-weight: 600;
    margin: 0;
  }
  .br-content-text {
    color: $gray-color-dark;
    font-size: $font-size-sm;
  }
  .br-empty {
    background: transparent;
    color: $gray-color-dark;
    .empty-title {
      color: $gray-color-900;
      font-size: $font-size;
      font-weight: 600;
    }
    .empty-subtitle {
      font-size: $font-size-sm;
    }
  }
  @media screen and (max-width: $size-md) {
    .br-layout {
      grid-template-columns: minmax(0, 1fr);
    }
    .br-side {
      border-right: 0;
      padding-right: 0;
    }
  }
}
</style>
